import React, {useEffect, useMemo, useState} from 'react'
import QuotaDowngradeBlockModal from './QuotaDowngradeBlockModal'
import {
  PreviewType,
  ProductPlan,
  ProductPlanTier,
  ProductQuotaResponseData,
  Subscription,
} from '../../../../../services/cellcast.v2.types'
import ChangePlanConfirmModal from './ChangePlanConfirmModal'
import PlanPaymentForm from '../../../../Components/PlanPaymentForm'

type PlanCardProps = {
  name: string
  price: string
  period: string
  quota: string
  features: string[]
  cta: string
  selected?: boolean
  isCurrent?: boolean
  highlight?: boolean
  onClick?: () => void
  onCardClick?: () => void
}

type ChangePlanDrawerProps = {
  show: boolean
  title: string
  onClose: () => void
  onBack?: () => void
  onUpdate?: (selectedPlan: ProductPlan | null) => void
  onUpdatePlan?: (selectedPlan: ProductPlan | null) => void
  onDataRefresh?: () => Promise<void>
  currentPlanLabel?: string
  plans?: ProductPlan[]
  currentPlanName?: string
  nextBillDateLabel?: string
  estimatedNextPaymentLabel?: string
  currentPlan?: ProductPlan
  subscription?: Subscription
  productQuota?: ProductQuotaResponseData
}

const borderGray = '#e5e7eb'
const textGray = '#6b7280'
const textDark = '#111827'
const purple = '#7c3aed'
const purpleLight = '#a78bfa'

const PlanCard: React.FC<PlanCardProps> = ({
  name,
  price,
  period,
  quota,
  features,
  cta,
  selected,
  isCurrent,
  highlight,
  onClick,
  onCardClick,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      className='rounded-3 border p-4'
      style={{
        borderColor: selected ? '#ddd6fe' : isHovered ? purple : borderGray,
        background: highlight
          ? 'linear-gradient(180deg, rgba(124,58,237,0.06), #ffffff)'
          : '#ffffff',
        boxShadow: highlight ? '0 1px 6px rgba(0,0,0,0.06)' : 'none',
        transition: 'border-color 120ms ease',
        cursor: 'pointer',
      }}
      onClick={onCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='d-flex align-items-start justify-content-between gap-3'>
        <div>
          <div className='d-flex align-items-center gap-2'>
            <div className='fs-2 fw-bolder' style={{color: textDark}}>
              {name}
            </div>
            {isCurrent && (
              <span
                className='badge rounded-pill'
                style={{background: '#f3f4f6', color: '#374151'}}
              >
                Current
              </span>
            )}
            {highlight && (
              <span
                className='badge rounded-pill'
                style={{background: '#ede9fe', color: '#6d28d9'}}
              >
                Recommended
              </span>
            )}
          </div>
          <div className='mt-1 text-muted fs-6'>{quota}</div>
        </div>
        <div className='text-end'>
          <div className='fs-2 fw-bolder' style={{color: textDark}}>
            {price}
          </div>
          <div style={{fontSize: 12, color: '#9ca3af'}}>{period}</div>
        </div>
      </div>
      <ul
        className='mt-3 mb-0'
        style={{listStyle: 'none', padding: 0, color: '#374151', fontSize: 14}}
      >
        {features.map((f) => (
          <li key={f} className='d-flex align-items-center mb-1'>
            <i className='bi bi-check-circle-fill check-icon-1'></i>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className='mt-4'>
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation()
            onClick?.()
          }}
          className={`w-100 btn ${selected ? 'btn-primary' : 'btn-outline btn-outline-primary'}`}
        >
          {cta}
        </button>
      </div>
    </div>
  )
}

const ChangePlanDrawer: React.FC<ChangePlanDrawerProps> = ({
  show,
  title,
  onClose,
  onBack,
  onUpdate,
  onDataRefresh,
  currentPlanLabel,
  plans = [],
  currentPlanName,
  nextBillDateLabel,
  estimatedNextPaymentLabel,
  currentPlan,
  subscription,
  onUpdatePlan,
  productQuota,
}) => {
  const [selectedName, setSelectedName] = useState<string | undefined>(currentPlanName)
  const [selectedPlan, setSelectedPlan] = useState<ProductPlan | null>(null)
  const [showChangePlanConfirm, setShowChangePlanConfirm] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [applyNow, setApplyNow] = useState<boolean>(true)
  const isTrial = subscription?.status === 'trialing'
  const [showQuotaBlock, setShowQuotaBlock] = useState(false)
  const [quotaBlockMsg, setQuotaBlockMsg] = useState('')
  const handleConfirmPlanChange = async (applyNowSelected: boolean) => {
    if (!selectedPlan) {
      setShowChangePlanConfirm(false)
      return
    }

    // Safety check: prevent downgrade when current usage exceeds new plan quota
    const currentUsage = productQuota?.quota_usage?.currentUsage ?? 0
    const newQuotaLimit = selectedPlan?.quotaLimit
    if (typeof newQuotaLimit === 'number' && currentUsage > newQuotaLimit) {
      setQuotaBlockMsg(
        `Your current usage (${currentUsage}) exceeds the selected plan limit (${newQuotaLimit}). Please delete some wallet cards before downgrading.`
      )
      setShowChangePlanConfirm(false)
      setShowQuotaBlock(true)
      return
    }
    setApplyNow(applyNowSelected)
    setShowChangePlanConfirm(false)
    setShowPaymentForm(true)
  }

  const uiPlans = useMemo(() => {
    if (!plans || plans.length === 0) {
      return []
    }

    return plans
      .filter((p) => p.name !== currentPlan?.name)
      .map((p) => ({
        name: p.name || 'Unknown Plan',
        price: `$${Math.floor(p.price || 0)}`,
        period: '/mo',
        quota: p.quotaLimit ? `${p.quotaLimit} ${p.name} included` : `${p.name} included`,
        features: Array.isArray(p.features) ? p.features : [],
        isCurrent: p.name === currentPlan?.name,
        selected: p.name === selectedName,
        plan: p,
      }))
  }, [plans, currentPlan?.name, selectedName])

  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    if (show) {
      const id = requestAnimationFrame(() => setAnimateIn(true))
      return () => cancelAnimationFrame(id)
    }
    setAnimateIn(false)
  }, [show])

  useEffect(() => {
    if (!show) return
    const prevOverflow = document.body.style.overflow
    const prevTouch = (document.body.style as any).touchAction
    document.body.style.overflow = 'hidden'
    ;(document.body.style as any).touchAction = 'none'
    return () => {
      document.body.style.overflow = prevOverflow
      ;(document.body.style as any).touchAction = prevTouch
    }
  }, [show])

  if (!show) return null

  return (
    <div className='position-relative'>
      <div
        className='position-fixed top-0 start-0 w-100 h-100'
        style={{
          background: 'rgba(0,0,0,0.5)',
          opacity: animateIn ? 1 : 0,
          transition: 'opacity 200ms ease',
          backdropFilter: 'blur(3px)',
          zIndex: 1040,
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className='position-fixed top-0 h-100 bg-white shadow'
        style={{
          right: 0,
          width: '100%',
          maxWidth: 640,
          zIndex: 1041,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          transform: animateIn ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms ease',
        }}
      >
        {/* Header */}
        <div
          className='border-bottom bg-white'
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            padding: '16px 24px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div className='d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center gap-3'>
              <h2 className='m-0' style={{fontSize: 20, fontWeight: 600, color: textDark}}>
                {title}
              </h2>
            </div>
            <button
              aria-label='Close'
              type='button'
              onClick={onClose}
              className='btn btn-light'
              style={{borderRadius: 999, color: '#6b7280'}}
            >
              ✕
            </button>
          </div>
          <p className='mb-0 mt-2' style={{fontSize: 14, color: '#6b7280'}}>
            Current plan:{' '}
            <span className='fw-medium' style={{color: textDark}}>
              {currentPlanLabel}
            </span>
          </p>
        </div>

        <div className='p-4 p-lg-5' style={{paddingTop: 24}}>
          <div
            className='rounded-3 p-4 mb-4'
            style={{border: `1px solid ${'#ede9fe'}`, background: '#f5f3ff'}}
          >
            <p className='mb-0' style={{fontSize: 14, color: '#1f2937'}}>
              ✨ Switch plans anytime. Changes apply immediately with fair{' '}
              <span className='fw-semibold'>pro-rata</span> credits.
            </p>
          </div>
          {isTrial && (
            <div
              className='rounded-3 p-4 mb-4'
              style={{border: `1px solid ${'#ede9fe'}`, background: '#f5f3ff'}}
            >
              <p className='mb-0' style={{fontSize: 14, color: '#1f2937'}}>
                👋 When you are on a trial, upgrading your plan will not incur{' '}
                <span className='fw-semibold'> any charges until the </span>
                trial ends.
              </p>
            </div>
          )}
          <div className='d-flex flex-column gap-3'>
            {uiPlans.length > 0 ? (
              uiPlans.map((p) => (
                <PlanCard
                  key={p.name}
                  name={p.name}
                  price={p.price}
                  period={p.period}
                  quota={p.quota}
                  features={p.features as string[]}
                  cta={
                    p.isCurrent
                      ? 'Keep current plan'
                      : p.price === '$0'
                      ? 'Downgrade'
                      : 'Choose plan'
                  }
                  selected={p.selected}
                  isCurrent={p.isCurrent}
                  highlight={p.plan.planKey === ProductPlanTier.PRO}
                  onClick={() => {
                    const currentUsage = productQuota?.quota_usage?.currentUsage ?? 0
                    const newQuotaLimit = (p.plan as any)?.quotaLimit ?? p.plan?.quotaLimit
                    if (typeof newQuotaLimit === 'number' && currentUsage > newQuotaLimit) {
                      setQuotaBlockMsg(
                        `Your current usage (${currentUsage}) exceeds the ${p.name} plan limit (${newQuotaLimit}). Please delete some wallet cards before downgrading.`
                      )
                      setShowQuotaBlock(true)
                      return
                    }

                    setSelectedName(p.name)
                    setSelectedPlan(p.plan)
                    onUpdatePlan && onUpdatePlan(p.plan)
                    setShowChangePlanConfirm(true)
                  }}
                  onCardClick={() => {
                    // Only allow card selection if not current plan
                    if (!p.isCurrent) {
                      setSelectedName(p.name)
                      setSelectedPlan(p.plan)
                      onUpdatePlan && onUpdatePlan(p.plan)
                    }
                  }}
                />
              ))
            ) : (
              <div className='text-center py-4'>
                <p style={{color: textGray, fontSize: 14}}>
                  {plans?.length === 0 ? 'No plans available' : 'Loading plans...'}
                </p>
              </div>
            )}
          </div>

          <div className='rounded-3 border p-4 mt-4' style={{borderColor: borderGray}}>
            <div className='d-flex align-items-start justify-content-between'>
              <div>
                <div className='fw-medium' style={{fontSize: 14, color: textDark}}>
                  Billing summary
                </div>
                <div className='mt-1' style={{fontSize: 12, color: textGray}}>
                  Next bill:{' '}
                  <span className='fw-medium' style={{color: textDark}}>
                    {nextBillDateLabel}
                  </span>
                </div>
                <div className='mt-1' style={{fontSize: 12, color: '#9ca3af'}}>
                  If you switch today, we'll apply a pro-rata credit for unused time on your current
                  plan.
                </div>
              </div>
              <div className='text-end'>
                <div style={{fontSize: 14, color: textGray}}>Estimated next payment</div>
                <div className='fw-semibold' style={{fontSize: 18, color: textDark}}>
                  {estimatedNextPaymentLabel}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className='border-top bg-white'
          style={{position: 'sticky', bottom: 0, padding: '16px 24px'}}
        >
          <div className='d-flex justify-content-end gap-3'>
            <button
              type='button'
              onClick={onClose}
              className='btn btn-outline btn-outline-secondary text-black'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={() => {
                if (selectedPlan) {
                  setShowChangePlanConfirm(true)
                }
              }}
              disabled={!selectedPlan}
              className={`btn btn-primary ${!selectedPlan ? 'disabled' : ''}`}
            >
              Update plan
            </button>
          </div>
        </div>

        <ChangePlanConfirmModal
          show={showChangePlanConfirm}
          onClose={async () => {
            setShowChangePlanConfirm(false)
            if (onDataRefresh) {
              await onDataRefresh()
            }

            onClose()
          }}
          onBack={() => {
            setShowChangePlanConfirm(false)
          }}
          onConfirm={handleConfirmPlanChange}
          currentPlan={currentPlan}
          newPlan={selectedPlan || null}
          nextBillingDate={nextBillDateLabel}
          isUpdate={false}
          subscription={subscription}
        />

        <PlanPaymentForm
          show={showPaymentForm}
          onClose={() => {
            setShowPaymentForm(false)
            if (onDataRefresh) {
              onDataRefresh()
            }
            onClose()
          }}
          plan={selectedPlan}
          applyNow={applyNow}
          onPaymentSuccess={async () => {
            if (!selectedPlan) {
              setShowPaymentForm(false)
              return
            }
            onUpdate && onUpdate(selectedPlan)
            setShowPaymentForm(false)

            if (onDataRefresh) {
              await onDataRefresh()
            }
            onClose()
          }}
          currentPlan={currentPlan}
          previewType={PreviewType.subscription_change}
          isUpdate={true}
          subscription={subscription}
        />
        {/* Quota downgrade blocker */}
        <QuotaDowngradeBlockModal
          show={showQuotaBlock}
          onClose={() => setShowQuotaBlock(false)}
          message={quotaBlockMsg}
        />
      </aside>
    </div>
  )
}

export default ChangePlanDrawer
